import { useEffect, useRef, useState } from 'react';
import { MOVIE_STUDIO_NAME } from '@/data/movieProduct';
import {
  runEditorCommand,
  type EditorCommandId,
} from '@/lib/editorCommands';

type MenuId = 'file' | 'edit' | 'object' | 'view' | 'motion' | 'help';

interface MenuItem {
  id?: EditorCommandId;
  label: string;
  shortcut?: string;
  danger?: boolean;
  separator?: boolean;
  /** Handled by the shell instead of the editor command bus. */
  action?: 'make-movie';
}

interface EditorMenuBarProps {
  projectName: string;
  isDirty: boolean;
  artboardLabel: string;
  leaving?: boolean;
  compact?: boolean;
  phone?: boolean;
  /** Design hides the Motion menu. Movie Studio omits the on/off switch. */
  studio?: 'design' | 'movie';
  onMakeMovie?: () => void;
  onClose: () => void;
  onArtboardSize: () => void;
}

const FILE_ITEMS: MenuItem[] = [
  { id: 'save', label: 'Save', shortcut: 'Ctrl+S' },
  { id: 'export', label: 'Export…', shortcut: 'Ctrl+E' },
  { id: 'present', label: 'Present', shortcut: 'F5' },
  { separator: true, label: '' },
  { id: 'artboard-size', label: 'Artboard size…' },
  { separator: true, label: '' },
  { id: 'close', label: 'Close' },
];

const EDIT_ITEMS: MenuItem[] = [
  { id: 'undo', label: 'Undo', shortcut: 'Ctrl+Z' },
  { id: 'redo', label: 'Redo', shortcut: 'Ctrl+Y' },
  { separator: true, label: '' },
  { id: 'cut', label: 'Cut', shortcut: 'Ctrl+X' },
  { id: 'copy', label: 'Copy', shortcut: 'Ctrl+C' },
  { id: 'paste', label: 'Paste', shortcut: 'Ctrl+V' },
  { id: 'duplicate', label: 'Duplicate', shortcut: 'Ctrl+D' },
  { separator: true, label: '' },
  { id: 'select-all', label: 'Select all', shortcut: 'Ctrl+A' },
  { id: 'delete', label: 'Delete', shortcut: 'Del', danger: true },
];

const OBJECT_ITEMS: MenuItem[] = [
  { id: 'group', label: 'Group', shortcut: 'Ctrl+G' },
  { id: 'ungroup', label: 'Ungroup', shortcut: 'Ctrl+Shift+G' },
  { separator: true, label: '' },
  { id: 'bring-front', label: 'Bring to front', shortcut: 'Ctrl+Shift+]' },
  { id: 'bring-forward', label: 'Bring forward', shortcut: 'Ctrl+]' },
  { id: 'send-backward', label: 'Send backward', shortcut: 'Ctrl+[' },
  { id: 'send-back', label: 'Send to back', shortcut: 'Ctrl+Shift+[' },
  { separator: true, label: '' },
  { id: 'flip-h', label: 'Flip horizontal' },
  { id: 'flip-v', label: 'Flip vertical' },
  { id: 'rotate-cw', label: 'Rotate 90° CW' },
  { id: 'rotate-ccw', label: 'Rotate 90° CCW' },
  { separator: true, label: '' },
  { id: 'align-left', label: 'Align left' },
  { id: 'align-center', label: 'Align center' },
  { id: 'align-right', label: 'Align right' },
  { id: 'align-top', label: 'Align top' },
  { id: 'align-middle', label: 'Align middle' },
  { id: 'align-bottom', label: 'Align bottom' },
];

const VIEW_ITEMS: MenuItem[] = [
  { id: 'zoom-in', label: 'Zoom in' },
  { id: 'zoom-out', label: 'Zoom out' },
  { id: 'zoom-100', label: 'Zoom to 100%', shortcut: 'Ctrl+0' },
  { separator: true, label: '' },
  { id: 'toggle-rulers', label: 'Rulers', shortcut: 'Ctrl+R' },
  { id: 'toggle-grid', label: 'Grid' },
  { separator: true, label: '' },
  { id: 'toggle-libraries', label: 'Libraries panel', shortcut: 'Ctrl+\\' },
  { id: 'toggle-inspector', label: 'Inspector panel', shortcut: 'Ctrl+Shift+\\' },
];

const MOTION_ITEMS: MenuItem[] = [
  { id: 'toggle-motion-mode', label: 'Motion mode' },
  { id: 'animation-panel', label: 'Animate layer…' },
  { separator: true, label: '' },
  { id: 'video-play-pause', label: 'Play / pause scene', shortcut: 'Space' },
  { id: 'video-stop', label: 'Stop' },
  { separator: true, label: '' },
  { id: 'scene-add', label: 'Add scene' },
  { id: 'scene-duplicate', label: 'Duplicate scene' },
  { id: 'scene-delete', label: 'Delete scene', danger: true },
  { separator: true, label: '' },
  { id: 'video-export', label: 'Export video…' },
];

const HELP_ITEMS: MenuItem[] = [
  { id: 'shortcuts', label: 'Keyboard shortcuts', shortcut: '?' },
];

function menusFor(studio: 'design' | 'movie'): { id: MenuId; label: string; items: MenuItem[] }[] {
  const fileItems: MenuItem[] =
    studio === 'design'
      ? [
          FILE_ITEMS[0],
          FILE_ITEMS[1],
          { action: 'make-movie', label: 'Make a movie' },
          ...FILE_ITEMS.slice(2),
        ]
      : FILE_ITEMS;
  const menus: { id: MenuId; label: string; items: MenuItem[] }[] = [
    { id: 'file', label: 'File', items: fileItems },
    { id: 'edit', label: 'Edit', items: EDIT_ITEMS },
    { id: 'object', label: 'Object', items: OBJECT_ITEMS },
    { id: 'view', label: 'View', items: VIEW_ITEMS },
  ];
  if (studio === 'movie') {
    const motionStart = MOTION_ITEMS.findIndex((item) => item.id === 'animation-panel');
    menus.push({
      id: 'motion',
      label: 'Motion',
      items: MOTION_ITEMS.slice(motionStart < 0 ? 0 : motionStart),
    });
  }
  menus.push({ id: 'help', label: 'Help', items: HELP_ITEMS });
  return menus;
}

export default function EditorMenuBar({
  projectName,
  isDirty,
  artboardLabel,
  leaving = false,
  compact = false,
  phone = false,
  studio = 'design',
  onMakeMovie,
  onClose,
  onArtboardSize,
}: EditorMenuBarProps) {
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const barRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  const menus = menusFor(studio);

  const runItem = (item: MenuItem) => {
    setOpenMenu(null);
    if (item.action === 'make-movie') {
      onMakeMovie?.();
      return;
    }
    if (!item.id) return;
    if (item.id === 'close') {
      onClose();
      return;
    }
    if (item.id === 'artboard-size') {
      onArtboardSize();
      return;
    }
    runEditorCommand(item.id);
  };

  return (
    <header className="ps-menubar" ref={barRef}>
      {phone ? (
        <>
          <button type="button" className="ps-menu-link" disabled={leaving} onClick={onClose}>
            {leaving ? 'Saving…' : 'Back'}
          </button>
          <div className="ps-doc-title">
            <span>{projectName}</span>
            {isDirty && <span className="dirty">●</span>}
          </div>
          <div className="ps-menubar-spacer" />
          <button
            type="button"
            className="ps-menu-link ps-menu-present"
            title="Present fullscreen (F5)"
            onClick={() => runEditorCommand('present')}
          >
            Present
          </button>
          <button type="button" className="ps-menu-link" onClick={() => runEditorCommand('save')}>
            Save
          </button>
          <button
            type="button"
            className="ps-menu-link"
            title="Export PNG/JPG (Ctrl+E)"
            onClick={() => runEditorCommand('export')}
          >
            Export
          </button>
          {studio === 'movie' && (
            <button
              type="button"
              className="ps-menu-link ps-menu-accent"
              title="Export the timeline as a video file"
              onClick={() => runEditorCommand('video-export')}
            >
              Video
            </button>
          )}
          {studio === 'design' && onMakeMovie && (
            <button type="button" className="ps-menu-link" onClick={onMakeMovie}>
              Movie
            </button>
          )}
        </>
      ) : (
        <>
          <div className="ps-brand">{studio === 'movie' ? MOVIE_STUDIO_NAME : 'Craftara'}</div>

          {menus.map((menu) => (
            <div
              key={menu.id}
              className={`ps-menu-item ps-menu-desktop ${openMenu === menu.id ? 'open' : ''}`}
              onMouseEnter={() => {
                if (openMenu) setOpenMenu(menu.id);
              }}
            >
              <button
                type="button"
                className="ps-menu-link"
                aria-haspopup="menu"
                aria-expanded={openMenu === menu.id}
                onClick={() => setOpenMenu(openMenu === menu.id ? null : menu.id)}
              >
                {menu.label}
              </button>
              {openMenu === menu.id && (
                <div className="ps-menu-dropdown" role="menu">
                  {menu.items.map((item, index) =>
                    item.separator ? (
                      <div key={`sep-${menu.id}-${index}`} className="ps-menu-sep" role="separator" />
                    ) : (
                      <button
                        key={item.id || `${menu.id}-${item.label}`}
                        type="button"
                        role="menuitem"
                        className={`ps-menu-option ${item.danger ? 'danger' : ''}`}
                        disabled={!item.id && !item.action}
                        onClick={() => runItem(item)}
                      >
                        <span>{item.label}</span>
                        {item.shortcut ? (
                          <span className="ps-menu-shortcut">{item.shortcut}</span>
                        ) : null}
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ))}

          {compact && (
            <button
              type="button"
              className="ps-menu-link ps-menu-compact"
              title="Inspector"
              onClick={() => runEditorCommand('toggle-inspector')}
            >
              Inspect
            </button>
          )}

          <div className="ps-menubar-spacer ps-menu-desktop" />

          <div className="ps-doc-title">
            <span>{projectName}</span>
            {isDirty && <span className="dirty">●</span>}
            <button
              type="button"
              className="ps-menu-link ps-menu-desktop"
              title="Change artboard size"
              onClick={onArtboardSize}
              style={{ color: 'var(--ps-muted)', fontWeight: 400 }}
            >
              {artboardLabel}
            </button>
          </div>

          <div className="ps-menubar-spacer" />

          <button type="button" className="ps-menu-link" disabled={leaving} onClick={onClose}>
            {leaving ? 'Saving…' : 'Close'}
          </button>
          <button type="button" className="ps-menu-link" onClick={() => runEditorCommand('save')}>
            Save
          </button>
          <button
            type="button"
            className="ps-menu-link"
            title="Present fullscreen (F5)"
            onClick={() => runEditorCommand('present')}
          >
            Present
          </button>
          <button
            type="button"
            className="ps-menu-link ps-menu-accent"
            title="Export PNG/JPG (Ctrl+E)"
            onClick={() => runEditorCommand('export')}
          >
            Export
          </button>
          {studio === 'movie' && (
            <button
              type="button"
              className="ps-menu-link ps-menu-accent"
              title="Export the timeline as a video file"
              onClick={() => runEditorCommand('video-export')}
            >
              Export video
            </button>
          )}
        </>
      )}
    </header>
  );
}
